import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity, Visitor } from '@atlaskit/adf-utils/types';

const ANCHOR_EXTENSION_KEY = 'anchor';
const ANCHOR_EXTENSION_TYPE = 'com.atlassian.confluence.macro.core';

type MacroParam = {
	value?: unknown;
};

type ExtensionAttrs = {
	extensionKey?: unknown;
	extensionType?: unknown;
	parameters?: {
		macroParams?: {
			''?: MacroParam;
			legacyAnchorId?: MacroParam;
		};
	};
};

const getStringParamValue = (param: MacroParam | undefined): string | null =>
	typeof param?.value === 'string' ? param.value : null;

export const sanitizeAnchorName = (rawValue: string): string => rawValue.replaceAll(/[%"'\s]/g, '');

const isAnchorMacroNode = (node: ADFEntity): boolean => {
	const attrs = node.attrs as ExtensionAttrs | undefined;
	return (
		attrs?.extensionKey === ANCHOR_EXTENSION_KEY && attrs.extensionType === ANCHOR_EXTENSION_TYPE
	);
};

const anchorMacroMatchesHash = (node: ADFEntity, hashId: string): boolean => {
	if (!isAnchorMacroNode(node)) {
		return false;
	}

	const macroParams = (node.attrs as ExtensionAttrs | undefined)?.parameters?.macroParams;
	const anchorName = getStringParamValue(macroParams?.['']);
	const legacyAnchorName = getStringParamValue(macroParams?.legacyAnchorId);

	return (
		// AnchorExtensionHandler strips these characters before rendering the current anchor name.
		(anchorName !== null && sanitizeAnchorName(anchorName) === hashId) ||
		// legacyAnchorId is already the stored URL fragment and is rendered without extra sanitizing.
		legacyAnchorName === hashId
	);
};

const frameContainsAnchorHash = (frame: ADFEntity, hashId: string): boolean => {
	let hasMatch = false;
	const visitExtension: Visitor = (node) => {
		if (hasMatch) {
			return;
		}

		hasMatch = anchorMacroMatchesHash(node, hashId);
	};

	traverse(frame, {
		inlineExtension: visitExtension,
	});

	return hasMatch;
};

export const findAnchorMacroFrameIndexByHash = (frames: unknown, hashId: string): number => {
	if (!Array.isArray(frames) || !frames.length || !hashId) {
		return -1;
	}

	// Find which Native Tabs frame owns the Anchor macro without relying on rendered DOM.
	return frames.findIndex((frame) => frameContainsAnchorHash(frame as ADFEntity, hashId));
};
